// views/Settings/GrowthOffersSettings.js
// First-order free delivery, its cap and the referral programme, per market
// (services/growth/growthOfferSettings.js on the server; founder decision
// 2 Nov 2026). The server decides every eligibility and amount; this screen
// only switches the offers and sets the cap. Both switches ship OFF.
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiGetGrowthOfferSettings, apiUpdateGrowthOfferSettings } from "../../api";
import { field, LastChanged, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";
import { AppButton } from "../../components/ui";

const MARKETS = [
  { code: "UG", label: "Uganda" },
  { code: "ZM", label: "Zambia" },
];

const SOURCE_LABEL = { setting: "saved here", env: "server environment", shipped: "shipped default" };
const onOff = (v) => (v ? "on" : "off");

// The form keeps only what the operator typed; a blank cap means "use the
// server default" and is sent as null, which clears the saved override.
const formFrom = (data) => ({
  firstOrderFreeDeliveryEnabled: !!data?.firstOrderFreeDelivery?.enabled,
  freeDeliveryCap: data?.overrides && "freeDeliveryCap" in data.overrides ? String(data.overrides.freeDeliveryCap) : "",
  referralEnabled: !!data?.referral?.enabled,
});

const GrowthOffersSettings = ({ canEdit = true }) => {
  const [country, setCountry] = useState("UG");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(formFrom(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  // Every change is audited with the reason it was made (server refuses without one).
  const [reason, setReason] = useState("");

  const load = async (cc) => {
    setLoading(true);
    try {
      const res = await apiGetGrowthOfferSettings(cc);
      setData(res.data);
      setForm(formFrom(res.data));
    } catch (err) {
      notify.fromError(err, "Could not load the free delivery offers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(country); }, [country]);

  const currency = data?.currency || (country === "ZM" ? "ZMW" : "UGX");
  const defaults = data?.defaults || {};
  const capText = form.freeDeliveryCap.trim();
  const capNumber = Number(capText);
  const capValid = capText === "" || (Number.isFinite(capNumber) && capNumber >= 0);

  // Only a value that differs from the server default is saved as an
  // override; one equal to it is sent as null, so the market keeps following
  // the server default (an env change included).
  const toggleOverride = (key) =>
    form[key] === !!defaults[key] ? null : form[key];
  const patch = () => ({
    firstOrderFreeDeliveryEnabled: toggleOverride("firstOrderFreeDeliveryEnabled"),
    referralEnabled: toggleOverride("referralEnabled"),
    freeDeliveryCap: capText === "" ? null : Math.floor(capNumber),
  });
  const unchanged = data && JSON.stringify(formFrom(data)) === JSON.stringify(form);

  const reasonValid = reason.trim().length >= 4;

  const save = async (e) => {
    e.preventDefault();
    if (!capValid) return notify.error("Enter a whole amount of 0 or more", "Leave the cap blank to use the server default.");
    if (!reasonValid) return notify.error("Give a reason for this change", "It is recorded in the audit log with your name.");
    setSaving(true);
    try {
      const res = await apiUpdateGrowthOfferSettings({ ...patch(), reason: reason.trim() }, country);
      setData(res.data);
      setForm(formFrom(res.data));
      setReason("");
      setSavedAt(Date.now());
      notify.success(
        "Free delivery offers saved",
        `${MARKETS.find((m) => m.code === country)?.label || country}: first order ${onOff(res.data.firstOrderFreeDelivery.enabled)}, referrals ${onOff(res.data.referral.enabled)}. Checkouts read it within about a minute.`,
      );
    } catch (err) {
      notify.fromError(err, "Could not save the free delivery offers");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !canEdit || saving || loading;
  const sourceOf = (key) => SOURCE_LABEL[data?.sources?.[key]] || "shipped default";

  return (
    <SettingsPanel
      icon="fa-gift"
      title="Free delivery offers"
      subtitle={loading ? "Loading..." : `First order ${onOff(data?.firstOrderFreeDelivery?.enabled)}, referrals ${onOff(data?.referral?.enabled)}, up to ${currency} ${Number(data?.firstOrderFreeDelivery?.cap || 0).toLocaleString()} per delivery`}
    >
      {!canEdit && <ReadOnlyNotice level="manage" />}
      <form onSubmit={save}>
        <SettingsPanel.Section title="What this does" icon="fa-circle-info">
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: C.textMid }}>
            Lusuku pays the delivery fee, up to the cap, on a customer's first order, on the first order of a
            friend who signed up with a referral code, and on one order for each friend whose first order was
            delivered. Only the delivery fee is waived: the goods, the vendor's commission and the VAT on the
            goods do not change, and the rider is paid the same as on any other delivery. The server decides
            who qualifies and claims each offer once, when the order is placed.
            <SettingHelp topic="growthOffersOverview" />
          </p>
        </SettingsPanel.Section>

        <SettingsPanel.Section title="Offers for each market" icon="fa-sliders-h">
          <div style={field.grid(2)}>
            <div>
              <label style={field.label}>Market<SettingHelp topic="growthOffersMarket" /></label>
              <select style={field.input} value={country} onChange={(e) => setCountry(e.target.value)} disabled={saving}>
                {MARKETS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={field.label}>
                Most Lusuku pays per free delivery ({currency})
                <SettingHelp topic="freeDeliveryCap" />
              </label>
              <input
                style={field.input}
                type="number" min="0" step="500" inputMode="numeric"
                placeholder={`Server default ${Number(defaults.freeDeliveryCap ?? 0).toLocaleString()}`}
                value={form.freeDeliveryCap}
                onChange={(e) => setForm((f) => ({ ...f, freeDeliveryCap: e.target.value }))}
                disabled={disabled}
              />
              <div style={field.help}>
                A fee above this is reduced by this much; a fee below it is free. Server default {currency} {Number(defaults.freeDeliveryCap ?? 0).toLocaleString()}. Leave blank to use it. 0 gives nothing away. Now from the {sourceOf("freeDeliveryCap")}.
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                <input
                  type="checkbox"
                  checked={form.firstOrderFreeDeliveryEnabled}
                  disabled={disabled}
                  onChange={(e) => setForm((f) => ({ ...f, firstOrderFreeDeliveryEnabled: e.target.checked }))}
                />
                Free delivery on a customer's first order
                <span onClick={(e) => e.preventDefault()}><SettingHelp topic="firstOrderFreeDelivery" /></span>
              </label>
              <div style={field.help}>Server default {onOff(defaults.firstOrderFreeDeliveryEnabled)}. Now from the {sourceOf("firstOrderFreeDeliveryEnabled")}.</div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                <input
                  type="checkbox"
                  checked={form.referralEnabled}
                  disabled={disabled}
                  onChange={(e) => setForm((f) => ({ ...f, referralEnabled: e.target.checked }))}
                />
                Referral programme: free deliveries both ways
                <span onClick={(e) => e.preventDefault()}><SettingHelp topic="referralProgramme" /></span>
              </label>
              <div style={field.help}>Server default {onOff(defaults.referralEnabled)}. Now from the {sourceOf("referralEnabled")}.</div>
            </div>
            {canEdit && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={field.label}>Reason for this change<SettingHelp topic="growthOffersReason" /></label>
                <input
                  style={field.input}
                  value={reason}
                  maxLength={500}
                  placeholder="For example: launch week promotion in Kampala"
                  onChange={(e) => setReason(e.target.value)}
                  disabled={saving || loading}
                />
                <div style={field.help}>Required to save. Recorded in the audit log with your name, the market and the values before and after.</div>
              </div>
            )}
          </div>
          <div style={field.actions}>
            {canEdit && <SaveButton saving={saving} disabled={!capValid || !reasonValid || unchanged || loading} />}
            {canEdit && data && Object.keys(data.overrides || {}).length > 0 && (
              <AppButton
                variant="quiet"
                icon="fas fa-rotate-left"
                onClick={() => setForm({
                  firstOrderFreeDeliveryEnabled: !!defaults.firstOrderFreeDeliveryEnabled,
                  freeDeliveryCap: "",
                  referralEnabled: !!defaults.referralEnabled,
                })}
                disabled={saving}
              >
                Use the server defaults
              </AppButton>
            )}
            <div style={{ marginLeft: "auto" }}>
              <LastChanged keys={[`growth_offers_${country}`]} refreshToken={savedAt} />
            </div>
          </div>
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default GrowthOffersSettings;
